import clsx from 'clsx';
import {assign, find, flatMap, forEach, get, keys, map, set, size, uniq, values} from 'lodash';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';

import Button from 'components/Button';
import RecommendationAnnotation from 'components/RecommendationAnnotation';
import KyError from 'components/errors/KyError';
import InfoLink from 'components/links/Info';
import Link from 'components/links/Link';
import FactSection from 'components/shared/fact_section';
import useAppContext from 'conf/AppContext';

import 'components/GenotypePicker/index.scss';

const propTypes = {
  annotationId: PropTypes.string.isRequired,
  note: PropTypes.element,
  source: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    gene: PropTypes.string,
    displaySingleAllele: PropTypes.bool,
    allowSingleAllele: PropTypes.bool,
    alleles: PropTypes.arrayOf(PropTypes.string),
    phenotypeOptionMap: PropTypes.object,
  })),
  chemicalId: PropTypes.string,
  gsiResults: PropTypes.object,
  gsiQuery: PropTypes.object,
};
/**
 * Displays fields for the user to pick a specific genotype which is then used to display specific recommendations.
 *
 * @param {object} props - props container
 * @param {string} props.annotationId - accession ID of the "parent" annotation
 * @param {*} props.note - optional note to display below picker
 * @param {string} props.source - optional data source string for source-specific content
 * @param {Array} props.options - an array of option data, one per gene
 * @param {string} props.chemicalId - optional, the specific chemical to show the recommendation for
 * @param {object} props.gsiResults - optional, results from a GSI query
 * @param {object} props.gsiQuery - optional, the chosen values from a GSI query in a map of gene symbol to 3-value array
 */
export default function GenotypePicker({annotationId, note, source,
  chemicalId, gsiResults, gsiQuery, options}) {
  const appContext = useAppContext();
  const [drugs, setDrugs] = useState(!gsiResults ? [] : map(values(gsiResults), (r) => r[source.toLowerCase()]));
  const [picks, setPicks] = useState({});
  const genes = map(options, (o) => o.genesymbol);

  useEffect(() => {
    const initPicks = {};
    forEach(genes, (gene) => {
      initPicks[gene] = [null, null, null];
    });
    setPicks(assign(initPicks, gsiQuery));
  }, [options, gsiQuery]);

  const onChange = (idx, symbol, allowSingleAllele = false) => async (e) => {
    const pickedAllele = e.target.value;
    const newPicks = set(picks || {}, `${symbol}.${idx}`, pickedAllele);
    setPicks(newPicks);
  };

  const handleSubmit = async () => {
    try {
      const r = await appContext.api.post(
        `site/gsi/${annotationId}/${chemicalId}`,
        {json: picks, parseJson: true},
      );
      setDrugs(find(values(r?.data?.drugs), (d) => d.drug.id === chemicalId) || null);
    } catch (err) {
      appContext.toastError(<KyError kyError={err} />);
    }
  };

  const handleClear = () => {
    setDrugs([]);
  };

  const pickerTitle = size(drugs) > 0
    ? 'Genotype-specific results'
    : 'Specify a genotype or phenotype for specific annotations';

  return (
    <FactSection headerClasses="mb-1" title={pickerTitle} id="tour-annotation-picker">
      <div className={clsx({'d-none': size(drugs) > 0})}>
        <div className="genotype-specific-annotations-picker">
          {map(options, (o) => <GeneControl key={o.genesymbol} onChange={onChange} picks={picks} {...o} />)}
          <Button actionHandler={handleSubmit}>Find Results</Button>
        </div>
        <small className="genotype-specific-annotations-picker_notes">
          <p className="font-weight-bold text-warning">Verify all entered information and recommendations that are retrieved before implementing in a healthcare setting</p>
          <p>Alleles not present in the above pull-down menus have no recommendation.</p>
          <DpwgWarning source={source} />
        </small>
        {note}
      </div>
      <PickerResults annotationId={annotationId} drugs={drugs} query={picks} onClear={handleClear} />
    </FactSection>
  );
}
GenotypePicker.propTypes = propTypes;

function PickerResults({drugs, query, annotationId, onClear}) {
  const resultGuideline = find(
    flatMap(values(drugs), (d) => d.annotations),
    (g) => g?.id === annotationId,
  );
  if (!resultGuideline) {
    return null;
  }

  let recommendationsToShow;
  const {genotypes, hasNoRecommendation, phenotypes: resultPhenotypes, recommendationAnnotations} = resultGuideline;
  if (hasNoRecommendation) {
    recommendationsToShow = <RecommendationAnnotation phenotypes={resultPhenotypes} genotypes={genotypes} />;
  } else if (size(recommendationAnnotations) > 0) {
    const multipopulation = size(uniq(map(recommendationAnnotations, (a) => a.population))) > 1;
    recommendationsToShow = map(recommendationAnnotations, (r) => <RecommendationAnnotation key={r.id} phenotypes={resultPhenotypes} genotypes={genotypes} multipopulation={multipopulation} {...r} />);
    recommendationsToShow.push(<Link href={`/genotypeResults?q=${encodeURIComponent(JSON.stringify(query))}`}>More annotations at the GSI</Link>);
  }

  return (
    <div className="guidelinePickerResultSection">
      {recommendationsToShow}
      <div className="mt-2"><Button actionHandler={onClear}>Change Genotype</Button></div>
    </div>
  );
}

function GeneControl({
  alleles, allowSingleAllele, displaySingleAllele, genesymbol, phenotypeOptionMap, onChange, picks,
}) {
  const genoDisabled = picks[genesymbol] && !!picks[genesymbol][2];
  const phenoDisabled = get(picks, genesymbol + '.0', false) || get(picks, genesymbol + '.1', false);

  const handleChange = (i) => onChange(i, genesymbol, allowSingleAllele);

  // if there are no phenotype options, don't show the control
  const showPhenotypes = size(phenotypeOptionMap) > 0;

  return (
    <>
      <div key={genesymbol}>{genesymbol}</div>
      <select key={`${genesymbol}s1`} onChange={handleChange(0)} disabled={genoDisabled}>
        <option value="">--</option>
        {map(alleles, (alleleName) =>
          <option selected={get(picks, genesymbol + '.0') === alleleName} value={alleleName} key={alleleName}>{alleleName}</option>)}
      </select>
      <div key={`${genesymbol}s2`}>
        {!displaySingleAllele && (
          <select onChange={handleChange(1)} disabled={genoDisabled}>
            <option value="">--</option>
            {map(alleles, (alleleName) =>
              <option selected={get(picks, genesymbol + '.1') === alleleName} value={alleleName} key={alleleName}>{alleleName}</option>)}
          </select>
        )}
      </div>
      <span key={`${genesymbol}s3`}>
        {showPhenotypes ? 'or' : ''}
      </span>
      <span key={`${genesymbol}s4`}>
        <select className={clsx({'d-none': !showPhenotypes})} onChange={handleChange(2)} disabled={phenoDisabled} defaultValue={get(picks, genesymbol + '.2')}>
          <option value="">--</option>
          {map(keys(phenotypeOptionMap), (gp) =>
            <PhenotypeOption key={gp} selected={get(picks, genesymbol + '.2') === phenotypeOptionMap[gp]} name={gp} score={phenotypeOptionMap[gp]} />)}
        </select>
      </span>
    </>
  );
}

function PhenotypeOption({name, score, selected = false}) {
  let value = name;
  if (score) {
    value = score;
  }
  return (
    <option
      selected={selected}
      value={value}
    >
      {name}
    </option>
  );
}

function DpwgWarning({source}) {
  if (source === 'DPWG') {
    return (
      <p>
        <InfoLink href="/page/dpwgMapping">Important information about alleles in DPWG guidelines.</InfoLink>
      </p>
    );
  } else {
    return null;
  }
}
